import { useEffect } from 'react';

// A bubbled 'click' only ever fires for the primary button, so secondary and
// middle presses never dismiss. The == null tolerance is for synthetic events
// without button info.
const isNonPrimaryButtonPress = (event) =>
  event.button != null && event.button !== 0;

// A press on a scrollbar — the document's, or a scrollable pane's — is a
// scroll, not a dismissal. A press on a classic scrollbar targets the
// scrolling element itself with clientX/Y or offsetX/Y beyond its client box.
const isScrollbarPress = (event) => {
  if (
    document.documentElement.clientWidth < event.clientX ||
    document.documentElement.clientHeight < event.clientY
  ) return true;
  const target = event.target;
  return (
    target instanceof Element &&
    ((target.clientWidth > 0 && target.clientWidth <= event.offsetX) ||
      (target.clientHeight > 0 && target.clientHeight <= event.offsetY))
  );
};

const elementContainsTarget = (element, target) =>
  Boolean(element && element.contains(target));

// Closes an open popper on a primary-button press outside it.
//
// MUI's ClickAwayListener can't do this job: it listens for bubbled clicks on
// the document, and several neighbors of the anchor (excerpt text, code tags,
// other add-code buttons) stopPropagation on their clicks, leaving the popper
// stuck open. A capture-phase pointerdown listener runs before any handler
// can cancel the event.
//
// - insideRef: ref to the popper's root node; presses inside it never close.
// - toggleRef: DOM node whose own click handler toggles `open` (see the
//   toggleRef prop comment in CodeSelector). Presses on it are left to that
//   handler — closing here too would make the same click reopen the popper.
// - onClose must be referentially stable (e.g. a setState function) so the
//   listener isn't re-attached mid-gesture.
export default function useCloseOnOutsidePress({ open, onClose, insideRef, toggleRef }) {
  useEffect(() => {
    if (!open) return undefined;

    // Cancels the deferred close of an in-flight touch gesture (see below).
    let cancelPendingTouch = null;
    const clearPendingTouch = () => {
      if (cancelPendingTouch) cancelPendingTouch();
      cancelPendingTouch = null;
    };

    // A touch that starts a scroll must not close the popper (the removed
    // ClickAwayListener's touchmove guard had the same rule). The browser
    // fires pointercancel when the gesture becomes a scroll and pointerup
    // when it stays a tap, so defer the close until we know which.
    const closeWhenTouchResolvesToTap = () => {
      clearPendingTouch();
      const closeOnUp = () => {
        clearPendingTouch();
        onClose();
      };
      document.addEventListener('pointerup', closeOnUp, true);
      document.addEventListener('pointercancel', clearPendingTouch, true);
      cancelPendingTouch = () => {
        document.removeEventListener('pointerup', closeOnUp, true);
        document.removeEventListener('pointercancel', clearPendingTouch, true);
      };
    };

    const handlePointerDown = (event) => {
      if (isNonPrimaryButtonPress(event)) return;
      if (isScrollbarPress(event)) return;
      if (elementContainsTarget(insideRef.current, event.target)) return;
      if (elementContainsTarget(toggleRef, event.target)) return;
      if (event.pointerType === 'touch') {
        closeWhenTouchResolvesToTap();
        return;
      }
      onClose();
    };

    document.addEventListener('pointerdown', handlePointerDown, true);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown, true);
      clearPendingTouch();
    };
  }, [open, onClose, insideRef, toggleRef]);
}
